(() => {
  const observed = new Set();
  const visible = new Set();
  const states = new WeakMap();
  let pageHidden = false;
  let interrupted = new WeakSet();
  let frame = null;
  let retryTimer = null;
  const eligible = (image) => !image.closest('.lch-card-secondary, [hidden], modal-dialog:not([open])');
  const sourceKey = (image) => `${image.getAttribute('src') || ''}\n${image.getAttribute('srcset') || ''}`;
  // Keep every existing image transform and responsive width. Changing only
  // query order gives a failed browser-cache entry a second request identity.
  const retryUrl = (value) => {
    try {
      const url = new URL(value, window.location.href);
      const shopifyImage = (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'))
        || (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/'));
      if (!shopifyImage || !/^https?:$/.test(url.protocol)) return value;
      const match = value.match(/^([^?#]+)\?([^#]+)(#.*)?$/);
      if (!match) return value;
      const parts = match[2].split('&');
      if (parts.length < 2) return value;
      return `${match[1]}?${parts.slice(1).concat(parts[0]).join('&')}${match[3] || ''}`;
    } catch (_error) {
      return value;
    }
  };
  const retrySrcset = (value) => value.replace(/(^|,)(\s*)(\S+)(\s+\d+(?:\.\d+)?[wx])(?=\s*(?:,|$))/g,
    (_match, comma, space, url, descriptor) => `${comma}${space}${retryUrl(url)}${descriptor}`);
  const retry = (image, state) => {
    if (state.retried || !sourceKey(image).trim()) return;
    state.retried = true;
    state.pendingSince = null;
    const srcset = image.getAttribute('srcset');
    const src = image.getAttribute('src');
    // A picture's source elements own selection; leave those sources intact.
    const pictureSource = image.closest('picture')?.querySelector('source[srcset]');
    if (srcset && !pictureSource) image.setAttribute('srcset', retrySrcset(srcset));
    if (src && !pictureSource) image.setAttribute('src', retryUrl(src));
    if (pictureSource) {
      if (srcset) image.setAttribute('srcset', srcset);
      else if (src) image.setAttribute('src', src);
    }
    // Our own source writes must not reset the retry budget in the observer.
    state.key = sourceKey(image);
  };
  const recover = () => {
    frame = null;
    if (pageHidden || document.hidden) return;
    if (retryTimer !== null) window.clearTimeout(retryTimer);
    retryTimer = null;
    let nextCheck = Infinity;
    const now = Date.now();
    for (const image of visible) {
      if (!image.isConnected || !eligible(image)) continue;
      const key = sourceKey(image);
      let state = states.get(image);
      if (!state || state.key !== key) {
        state = { key, retried: false, pendingSince: null };
        states.set(image, state);
      }
      if (image.complete && image.naturalWidth > 0) {
        state.pendingSince = null;
        continue;
      }
      if (image.fetchPriority === 'low') image.fetchPriority = 'auto';
      if (image.loading === 'lazy') image.loading = 'eager';
      if (image.complete && image.naturalWidth === 0) retry(image, state);
      else if (interrupted.has(image) && image.naturalWidth === 0 && !state.retried) {
        if (state.pendingSince === null) state.pendingSince = now;
        const remaining = 10000 - (now - state.pendingSince);
        if (remaining <= 0) retry(image, state);
        else nextCheck = Math.min(nextCheck, remaining);
      }
    }
    if (Number.isFinite(nextCheck)) retryTimer = window.setTimeout(recover, nextCheck);
  };
  const schedule = () => {
    if (pageHidden || document.hidden || frame !== null) return;
    frame = window.requestAnimationFrame(recover);
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    const snapshots = entries.map((entry) => ({ entry, visibility: getComputedStyle(entry.target).visibility }));
    for (const { entry, visibility } of snapshots) {
      if (entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0
        && visibility === 'visible') visible.add(entry.target);
      else visible.delete(entry.target);
    }
    schedule();
  }) : null;
  const measure = () => {
    const snapshots = Array.from(observed, (image) => ({ image, rect: image.getBoundingClientRect(),
      visibility: getComputedStyle(image).visibility }));
    visible.clear();
    for (const { image, rect, visibility } of snapshots) {
      if (visibility === 'visible' && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
        && rect.top < window.innerHeight && rect.left < window.innerWidth) visible.add(image);
    }
    recover();
  };
  const refresh = (reset = false) => {
    const current = new Set(Array.from(document.querySelectorAll('#MainContent img')).filter(eligible));
    for (const image of observed) {
      if (current.has(image)) continue;
      observer?.unobserve(image);
      image.removeEventListener('load', schedule);
      image.removeEventListener('error', schedule);
      observed.delete(image);
      visible.delete(image);
    }
    for (const image of current) {
      if (!observed.has(image)) {
        observed.add(image);
        image.addEventListener('load', schedule);
        image.addEventListener('error', schedule);
        observer?.observe(image);
      } else if (reset) {
        observer?.unobserve(image);
        observer?.observe(image);
      }
    }
    if (!observer && frame === null && !pageHidden && !document.hidden) frame = window.requestAnimationFrame(measure);
    else schedule();
  };
  const pause = () => {
    if (frame !== null) window.cancelAnimationFrame(frame);
    if (retryTimer !== null) window.clearTimeout(retryTimer);
    frame = null;
    retryTimer = null;
  };
  const onScroll = () => { if (observer) schedule(); else refresh(); };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => refresh(true), { passive: true });
  window.addEventListener('pagehide', () => {
    pageHidden = true;
    interrupted = new WeakSet();
    for (const image of observed) {
      if (!image.complete && image.naturalWidth === 0 && sourceKey(image).trim()) {
        interrupted.add(image);
      }
    }
    pause();
  });
  window.addEventListener('pageshow', (event) => {
    pageHidden = false;
    const historyReturn = event.persisted
      || window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
    if (historyReturn) {
      for (const image of observed) {
        const state = states.get(image);
        if (state) { state.retried = false; state.pendingSince = null; }
        if (!image.complete && image.naturalWidth === 0 && sourceKey(image).trim()) {
          interrupted.add(image);
        }
      }
    }
    refresh(true);
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) pause(); else refresh(true); });
  const watch = () => {
    const main = document.querySelector('#MainContent');
    if (main) new MutationObserver(() => refresh()).observe(main,
      { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open', 'src', 'srcset'] });
    refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch, { once: true });
  else watch();
})();

(() => {
  const completed = new WeakSet();
  let pageReady = document.readyState === 'complete';
  let pageHidden = false;
  let busy = false;
  let timer = null;
  let idle = null;
  let activeUntil = 0;
  let resumeJob = null;
  let cancelJob = null;
  let mediaPool = [];
  let candidatePool = [];
  const measured = new Map();
  const waiting = new Set();
  const observed = new Set();
  const intersection = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    const scrollTop = window.scrollY || 0;
    const scrollLeft = window.scrollX || 0;
    // IntersectionObserver supplies geometry after layout. Do not synchronously
    // measure the whole page from each idle callback or while warming an image.
    for (const entry of entries) {
      const rect = entry.boundingClientRect;
      measured.set(entry.target, {
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft,
        width: rect.width,
        height: rect.height,
        visibility: getComputedStyle(entry.target).visibility,
      });
      waiting.delete(entry.target);
    }
    schedule();
  }, { threshold: [0, 0.25, 1] }) : null;

  const refresh = () => {
    mediaPool = Array.from(document.querySelectorAll('img, video'));
    candidatePool = Array.from(document.querySelectorAll('#MainContent img[loading="lazy"], #MainContent lch-product-video'));
    if (!intersection) return;
    const targets = new Set([...mediaPool, ...candidatePool]);
    for (const target of observed) {
      intersection.unobserve(target);
      if (!targets.has(target)) {
        observed.delete(target);
        measured.delete(target);
        waiting.delete(target);
      }
    }
    for (const target of targets) {
      observed.add(target);
      waiting.add(target);
      intersection.observe(target);
    }
  };

  const readMedia = () => {
    if (intersection && waiting.size) return null;
    const snapshots = new Map();
    const scrollTop = window.scrollY || 0;
    const scrollLeft = window.scrollX || 0;
    for (const media of new Set([...mediaPool, ...candidatePool])) {
      if (media.closest('.lch-card-secondary') || media.closest('[hidden], modal-dialog:not([open])')) continue;
      if (intersection) {
        const cached = measured.get(media);
        if (!cached || cached.visibility !== 'visible') continue;
        const top = cached.top - scrollTop;
        const left = cached.left - scrollLeft;
        snapshots.set(media, { media, rect: { top, left, width: cached.width, height: cached.height,
          right: left + cached.width, bottom: top + cached.height } });
      } else if (getComputedStyle(media).visibility === 'visible') {
        // Legacy browsers without IntersectionObserver: one shared read pass,
        // rather than measuring the same media in two separate scans.
        snapshots.set(media, { media, rect: media.getBoundingClientRect() });
      }
    }
    return snapshots;
  };

  const visible = (rect, minimumRatio = 0) => {
    if (rect.width <= 0 || rect.height <= 0) return false;
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return width * height > 0 && width * height >= rect.width * rect.height * minimumRatio;
  };

  const schedule = () => {
    if (!pageReady || pageHidden || document.hidden || busy || timer !== null || idle !== null || navigator.connection?.saveData) return;
    timer = window.setTimeout(() => {
      timer = null;
      if ('requestIdleCallback' in window) idle = window.requestIdleCallback(run, { timeout: 800 });
      else run();
    }, 200);
  };

  const finish = () => { busy = false; schedule(); };

  const warmImage = (image) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resumeJob = null;
      cancelJob = null;
      image.removeEventListener('load', settle);
      image.removeEventListener('error', settle);
      finish();
    };
    cancelJob = settle;
    resumeJob = () => { if (image.complete) settle(); };
    image.addEventListener('load', settle, { once: true });
    image.addEventListener('error', settle, { once: true });
    image.fetchPriority = 'low';
    image.loading = 'eager';
    if (image.complete) settle();
  };

  const warmVideo = (element, width) => {
    const video = element.querySelector('video');
    if (!video || typeof element.loadSource !== 'function') { finish(); return; }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resumeJob = null;
      cancelJob = null;
      video.removeEventListener('progress', check);
      video.removeEventListener('canplaythrough', check);
      video.removeEventListener('suspend', settle);
      video.removeEventListener('error', settle);
      finish();
    };
    const check = () => {
      if (Number.isFinite(video.duration) && video.buffered.length
        && video.buffered.end(video.buffered.length - 1) >= video.duration - 0.25) settle();
    };
    cancelJob = settle;
    resumeJob = check;
    video.addEventListener('progress', check);
    video.addEventListener('canplaythrough', check);
    video.addEventListener('suspend', settle, { once: true });
    video.addEventListener('error', settle, { once: true });
    element.displayWidth = width;
    video.preload = 'auto';
    if (!element.loadSource()) settle();
    else check();
  };

  const run = () => {
    idle = null;
    if (!pageReady || pageHidden || document.hidden || busy || navigator.connection?.saveData) return;
    if (Date.now() < activeUntil || document.fonts?.status === 'loading') { schedule(); return; }
    const snapshots = readMedia();
    if (!snapshots) { schedule(); return; }
    const initialMedia = mediaPool.map((media) => snapshots.get(media)).filter(Boolean);
    if (initialMedia.some(({ media, rect }) => visible(rect, media.tagName === 'VIDEO' ? 0.25 : 0)
      && (media.tagName === 'IMG' ? !media.complete : media.readyState < 3))) { schedule(); return; }
    const candidates = candidatePool.filter((media) => !completed.has(media))
      .map((media) => snapshots.get(media)).filter(Boolean)
      .filter(({ media, rect }) => rect.width > 0 && rect.height > 0 && !visible(rect, media.tagName === 'IMG' ? 0 : 0.25)
        && (media.tagName === 'IMG' ? !media.complete : !media.querySelector('video')?.hasAttribute('src')))
      .sort((a, b) => Math.abs(a.rect.top - window.innerHeight) - Math.abs(b.rect.top - window.innerHeight)
        || (a.media.tagName === 'IMG' ? -1 : 1));
    const next = candidates[0];
    if (!next) return;
    completed.add(next.media);
    busy = true;
    if (next.media.tagName === 'IMG') warmImage(next.media);
    else warmVideo(next.media, next.rect.width);
  };

  const pauseSchedule = () => {
    if (timer !== null) window.clearTimeout(timer);
    if (idle !== null) window.cancelIdleCallback(idle);
    timer = null;
    idle = null;
  };
  const onInteraction = () => { activeUntil = Date.now() + 150; pauseSchedule(); schedule(); };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => { refresh(); schedule(); }, { passive: true });
  window.addEventListener('pointerdown', onInteraction, { passive: true });
  window.addEventListener('keydown', onInteraction);
  window.addEventListener('pagehide', () => {
    pageHidden = true;
    pauseSchedule();
    cancelJob?.();
  });
  window.addEventListener('pageshow', () => {
    pageHidden = false;
    pageReady = true;
    refresh();
    resumeJob?.();
    schedule();
  });
  document.addEventListener('visibilitychange', () => { if (document.hidden) pauseSchedule(); else schedule(); });
  window.addEventListener('load', () => { pageReady = true; schedule(); }, { once: true });
  document.fonts?.ready.then(() => { refresh(); schedule(); });
  const watch = () => {
    const main = document.querySelector('#MainContent');
    if (main) new MutationObserver(() => { refresh(); schedule(); }).observe(main,
      { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open'] });
    refresh();
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch, { once: true });
  else watch();
})();
